import React, { useEffect, useRef } from "react";
import { Button, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { DELIVERY_DOCKET, STATUS, TEXT_APPLY, TEXT_CLEAR, TEXT_FILTERS } from "@/constants/strings.ts";
import { DropdownSelector } from "@/components/dropdown/DropDown.tsx";
import { useViewJobItemDetailStore } from "@/stores/viewJobItemDetailStore.ts";
import { useShallow } from "zustand/shallow";
import { THEME } from "@/constants";

interface ViewJobItemDetailItemFiltersProps {
  isVisible: boolean;
  onCancel: () => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
}

export const ViewJobItemDetailItemFiltersModal: React.FC<ViewJobItemDetailItemFiltersProps> = ({
  isVisible,
  onCancel,
  onClearFilters,
  onApplyFilters
}) => {
  const { filters, selectedStatus, setSelectedStatus, selectedDeliveryDocket, setSelectedDeliveryDocket } = useViewJobItemDetailStore(
    useShallow(state => ({
      filters: state.filters,
      selectedStatus: state.selectedStatus,
      setSelectedStatus: state.setSelectedStatus,
      selectedDeliveryDocket: state.selectedDeliveryDocket,
      setSelectedDeliveryDocket: state.setSelectedDeliveryDocket
    }))
  );

  // Store the original state when modal opens to restore on cancel
  const originalStateRef = useRef<{
    selectedStatus: string[];
    selectedDeliveryDocket: string[];
  } | null>(null);

  // Effect to handle modal open/close logic
  useEffect(() => {
    if (isVisible) {
      // Store current state when modal opens
      originalStateRef.current = {
        selectedStatus: [...selectedStatus],
        selectedDeliveryDocket: [...selectedDeliveryDocket]
      };

      // Check if there are any applied filters (not all options selected)
      const allStatusIds = filters.statuses?.map(s => s.id) || [];
      const allDeliveryIds = filters.deliveryNumbers?.map(d => d.id) || [];

      const hasAppliedFilters =
        (selectedStatus.length > 0 && selectedStatus.length < allStatusIds.length) ||
        (selectedDeliveryDocket.length > 0 && selectedDeliveryDocket.length < allDeliveryIds.length);

      if (!hasAppliedFilters) {
        // Reset to all options selected if no previous filters were applied
        setSelectedStatus(allStatusIds);
        setSelectedDeliveryDocket(allDeliveryIds);
      }
    }
  }, [isVisible, filters.statuses, filters.deliveryNumbers]);

  const clearFilters = () => {
    onCancel();
    onClearFilters();
  };

  const applyFilters = () => {
    onCancel();
    onApplyFilters();
  };

  const handleCancel = () => {
    // Restore original state when canceling without applying
    if (originalStateRef.current) {
      setSelectedStatus(originalStateRef.current.selectedStatus);
      setSelectedDeliveryDocket(originalStateRef.current.selectedDeliveryDocket);
    }
    onCancel();
  };

  return (
    <Modal
      open={isVisible}
      title={<span className="text-lg font-semibold text-gray-900">{TEXT_FILTERS}</span>}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={clearFilters} style={{ height: "40px" }}>
          {TEXT_CLEAR}
        </Button>,
        <Button key="apply" type="primary" onClick={applyFilters} style={{ backgroundColor: THEME.COLORS.PRIMARY, height: "40px" }}>
          {TEXT_APPLY}
        </Button>
      ]}
      styles={{
        body: {
          paddingTop: 24
        },
        footer: {
          paddingTop: 24
        }
      }}
      centered
      closeIcon={<CloseOutlined className="text-gray-700 cursor-pointer" />}
      width={400}
      wrapClassName="bottom-modal-wrapper"
      maskClosable={false}
    >
      <div>
        <div>
          <DropdownSelector title={STATUS} options={filters.statuses} selected={selectedStatus} onSelect={setSelectedStatus} canSearch={false} />
        </div>

        <div className="mt-3">
          <DropdownSelector
            title={DELIVERY_DOCKET}
            options={filters.deliveryNumbers}
            selected={selectedDeliveryDocket}
            onSelect={setSelectedDeliveryDocket}
          />
        </div>
      </div>
    </Modal>
  );
};
